import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { 
  Table, 
  TableHead, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow, 
  Paper, 
  Button, 
  Typography 
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Device } from '@twilio/voice-sdk';

import ErrorDialog from '../ErrorDialog';
import YesNoDialog from '../YesNoDialog';
import EventStatus from './EventStatus';
import { Bingo, Card } from './Bingo';
import { AuthContext } from '../AuthContextProvider';
import { rpc, fetchJson , fetchWithTimeout } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import { usePreventReload } from '../utils/usePreventReload';
import '../App.css';

const MAX_RETRIES = 3;

const BingoEvent = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  // Event & User State
  const [scheduledEvent, setScheduledEvent] = useState(null);
  const [eventUsers, setEventUsers] = useState([]);
  const [status, setStatus] = useState(Constants.EVENT_NOT_READY);

  // Teleconference & Call State
  const [conferenceStarted, setConferenceStarted] = useState(false);
  const [potsSpeaker, setPotsSpeaker] = useState('');
  const [potsStatus, setPotsStatus] = useState({});
  const [hasMuted, setHasMuted] = useState({});
  const [hungUpPhones, setHungUpPhones] = useState({});

  // Bingo State
  const [lastBall, setLastBall] = useState(null);
  const [ballsChosen, setBallsChosen] = useState('');

  // Dialog & Error State
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openConfirmCancelDialog, setOpenConfirmCancelDialog] = useState(false);
  const [openConfirmNewGameDialog, setOpenConfirmNewGameDialog] = useState(false);
  const [openConfirmEndConferenceDialog, setOpenConfirmEndConferenceDialog] = useState(false);

  // Dynamic Instance Refs
  const bingoGame = useRef(null);
  const bingoCards = useRef({});
  const wsClient = useRef(null);
  const twilioDevice = useRef(null);
  const pollingTimer = useRef(null);
  const retryCount = useRef(0);
  const isManualDisconnect = useRef(false);
  const isConnecting = useRef(false);
  const wsReconnectTimer = useRef(null);
  const freshParams = useRef({});

  // Helper Functions
  const getPhoneNumber = useCallback((u) => {
    return u?.phone ? u.phone.replace(/\D/g, '') : '';
  }, []);

  const getPhoneNumbersToDial = useCallback(() => {
    return eventUsers
      .filter((u) => !u.useTwilio)
      .map((u) => {
        const phone = getPhoneNumber(u);
        const muted = !!hasMuted[u.email];
        return `${phone}:${muted}:${u.email}`;
      })
      .join(', ');
  }, [eventUsers, hasMuted, getPhoneNumber]);

  // Prevent browser back button navigation during active session
  usePreventReload(true);
  useEffect(() => {
    window.history.pushState(null, null, window.location.href);
    window.onpopstate = () => window.history.go(1);
  }, []);

  // Keep fresh parameters synchronized for Twilio call initialization
  useEffect(() => {
    freshParams.current = {
      To: getPhoneNumbersToDial(),
      site: user?._site?._id,
      eventid: scheduledEvent?._id,
    };
  }, [getPhoneNumbersToDial, scheduledEvent, user]);

  // Pushstate history lock replacement
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.go(1);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Fetch Event Details
  useEffect(() => {
    if (!id) return;
    rpc({
      method: 'GetScheduledEvent',
      params: { _id: id },
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess: (data) => {
        setScheduledEvent(data);
        if (data) {
          setStatus(EventStatus.getEventStatus(data));
        }
      },
    });
  }, [id]);

  // Fetch Event Groups / Associated Users
  useEffect(() => {
    if (!scheduledEvent || !scheduledEvent._eventGroups) return;

    setStatus(EventStatus.getEventStatus(scheduledEvent));

    const fetchUsersInGroup = async (groupId) => {
      rpc({
        method: 'GetUsers',
        params: { _eventGroups: groupId },
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: (usersInGroup) => {
          setEventUsers((prevUsers) => {
            const combined = [...prevUsers, ...usersInGroup];
            const seen = new Set();
            return combined.filter((u) => {
              const duplicate = seen.has(u._id);
              seen.add(u._id);
              return !duplicate;
            });
          });
        },
      });
    };

    scheduledEvent._eventGroups.forEach((groupId) => {
      fetchUsersInGroup(groupId);
    });
  }, [scheduledEvent]);

  // Periodic Status Polling
  useEffect(() => {
    if (!scheduledEvent) return;

    const timer = setInterval(() => {
      setStatus(EventStatus.getEventStatus(scheduledEvent));
    }, 5000);

    return () => clearInterval(timer);
  }, [scheduledEvent]);

  // Remote Status Mutations
  const setEventStatus = async ({ status: newStatus, ball }) => {
    if (!scheduledEvent) return;

    EventStatus.setEventStatus(scheduledEvent, newStatus);
    setStatus(newStatus);

    const params = {
      _id: scheduledEvent._id,
      status: newStatus,
    };

    if (newStatus === Constants.EVENT_BINGO_GAME) {
      params.bingoGame = JSON.stringify(bingoCards.current);
    } else if (newStatus === Constants.EVENT_BINGO_BALL && ball !== undefined) {
      params.bingoBall = ball.toString();
    }

    await rpc({
      method: 'UpdateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess: () => {
        console.log('UpdateScheduledEvent success');
      },
    });
  };

  // Teleconference Controls
  const stopPollingForUserStatus = useCallback(() => {
    if (pollingTimer.current) clearInterval(pollingTimer.current);
    if (wsReconnectTimer.current) clearTimeout(wsReconnectTimer.current);
    if (wsClient.current) {
      wsClient.current.onclose = null;
      wsClient.current.close();
      wsClient.current = null;
    }
  }, []);

  const hangup = useCallback(
    async (eventUser) => {
      try {
        const phone = getPhoneNumber(eventUser);
        setHungUpPhones((prev) => ({ ...prev, [phone]: true }));

        const params = new URLSearchParams({
          site: user?._site?._id,
          number: phone,
          email: eventUser.email,
          eventid: scheduledEvent?._id,
        });

        await fetchJson(`/itvs-site/delete?${params.toString()}`, {
          headers: { Accept: 'application/json' },
        });
      } catch (err) {
        console.error(err);
        setErrorMessage(`Hangup failed: ${err.message || err}`);
        setOpenErrorDialog(true);
      }
    },
    [getPhoneNumber, scheduledEvent, user]
  );

  const hangupAll = useCallback(() => {
    eventUsers.forEach((u) => {
      if (!u.useTwilio) {
        hangup(u);
      }
    });
  }, [eventUsers, hangup]);

  const endConference = useCallback(() => {
    isManualDisconnect.current = true;
    isConnecting.current = false;
    retryCount.current = 0;

    stopPollingForUserStatus();
    setOpenConfirmEndConferenceDialog(false);
    setConferenceStarted(false);
    setPotsSpeaker('');
    setPotsStatus({});

    if (twilioDevice.current) {
      try {
        twilioDevice.current.disconnectAll();
        twilioDevice.current.destroy();
      } catch (e) {
        console.error('Error destroying Twilio Device:', e);
      }
      twilioDevice.current = null;
    }

    hangupAll();
  }, [stopPollingForUserStatus, hangupAll]);

  const startPollingForUserStatus = useCallback(() => {
    stopPollingForUserStatus();

    const connectWs = () => {
      if (isManualDisconnect.current) return;

      wsClient.current = new WebSocket(Constants.WEBSOCKET_URL);

      wsClient.current.onopen = () => {
        console.log('WebSocket Client Connected');
        pollingTimer.current = setInterval(() => {
          if (wsClient.current?.readyState === WebSocket.OPEN) {
            wsClient.current.send(
              JSON.stringify({
                type: 'getPotsSpeaker',
                site: user?._site?._id,
                eventid: scheduledEvent?._id,
              })
            );
          }
        }, 1000);
      };

      wsClient.current.onmessage = (message) => {
        try {
          const m = JSON.parse(message.data);
          if (m.type === 'getPotsSpeaker') {
            setPotsSpeaker(m.speaker);
            setPotsStatus(m.status);
          }
        } catch (e) {
          console.error('Failed to parse WS message:', e);
        }
      };

      wsClient.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsClient.current.onclose = () => {
        if (pollingTimer.current) clearInterval(pollingTimer.current);

        if (!isManualDisconnect.current) {
          wsReconnectTimer.current = setTimeout(() => {
            console.log('Attempting WebSocket reconnect...');
            connectWs();
          }, 3000);
        }
      };
    };

    connectWs();
  }, [stopPollingForUserStatus, user, scheduledEvent]);

  const startConference = async () => {
    if (isConnecting.current) return;
    isConnecting.current = true;
    isManualDisconnect.current = false;

    try {
      const token = await fetchJson(`/itvs-site/capability-token?identity=${encodeURIComponent(user.email)}`, {
        headers : { 'Accept': 'application/json' },
      });

      if (twilioDevice.current) {
        twilioDevice.current.destroy();
        twilioDevice.current = null;
      }

      twilioDevice.current = new Device(token.token, {
        logLevel: 'warn',
        closeProtection: true,
        edge: ['ashburn', 'roaming'],
        maxCallSignalingTimeoutMs: 30000,
      });

      twilioDevice.current.on('error', (error) => {
        console.error('Twilio Device Error:', error);
        const isAuthError = error.code === 31204 || error.code === 31205;

        if (!isManualDisconnect.current && !isAuthError && retryCount.current < MAX_RETRIES) {
          retryCount.current += 1;
          isConnecting.current = false;
          const delay = Math.min(1000 * Math.pow(2, retryCount.current), 8000);
          console.warn(`Reconnecting attempt ${retryCount.current} in ${delay}ms...`);
          setTimeout(() => startConference(), delay);
        } else if (retryCount.current >= MAX_RETRIES) {
          setErrorMessage('Connection lost. Maximum reconnect attempts reached.');
          setOpenErrorDialog(true);
          endConference();
        }
      });

      twilioDevice.current.on('registered', () => {
        retryCount.current = 0;
      });

      const callParams = {
        ...freshParams.current,
        Initialize: true,
      };

      const call = await twilioDevice.current.connect({ params: callParams });
      setConferenceStarted(true);
      isConnecting.current = false;

      call.on('reconnecting', (error) => {
        console.warn('Media connection fluctuating. Reconnecting call...', error);
      });

      call.on('reconnected', () => {
        console.log('Media connection restored.');
      });

      call.on('disconnect', () => {
        if (!isManualDisconnect.current && retryCount.current < MAX_RETRIES) {
          twilioDevice.current?.emit('error', { message: 'Unexpected call disconnect' });
        }
      });

      startPollingForUserStatus();
      setTimeout(() => {
        setEventStatus({ status: Constants.EVENT_TELECON_STARTED });
      }, 1000);
    } catch (err) {
      isConnecting.current = false;
      console.error('Unexpected Error in startConference:', err);
      setErrorMessage(err.message || 'Failed to start conference');
      setOpenErrorDialog(true);
    }
  };

  const redial = async (eventUser) => {
    try {
      const phone = getPhoneNumber(eventUser);
      const muted = !!hasMuted[eventUser.email];
      setHungUpPhones((prev) => ({ ...prev, [phone]: false }));

      const to = `${phone}:${muted}:${eventUser.email}`;
      const params = new URLSearchParams({
        site: user?._site?._id,
        eventid: scheduledEvent?._id,
        To: to,
      });

      await fetchWithTimeout('/itvs-site/conference2', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: params,
      });
    } catch (err) {
      console.error(err);
      setErrorMessage(`Failed to redial: ${err.message || err}`);
      setOpenErrorDialog(true);
    }
  };

  // Mute Controls
  const muteOrUnmute = async (targetStatus, eventUser) => {
    try {
      const isMuted = targetStatus === Constants.USER_MUTED;
      setHasMuted((prev) => ({
        ...prev,
        [eventUser.email]: isMuted,
      }));

      await rpc({
        method: 'UpdateUser',
        params: {
          _id: eventUser._id,
          status: targetStatus,
        },
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: (updatedUser) => {
          console.log('UpdateUser success', updatedUser);
        },
      });

      if (!eventUser.useTwilio) {
        const params = new URLSearchParams({
          site: user?._site?._id,
          number: getPhoneNumber(eventUser),
          muted: isMuted,
          email: eventUser.email,
          eventid: scheduledEvent?._id,
        });

        await fetchJson(`/itvs-site/mute?${params.toString()}`, {
          headers: { Accept: 'application/json' },
        });
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(`muteOrUnmute failed: ${err.message || err}`);
      setOpenErrorDialog(true);
    }
  };

  const muteAll = () => {
    eventUsers.forEach((u) => muteOrUnmute(Constants.USER_MUTED, u));
  };

  const unMuteAll = () => {
    eventUsers.forEach((u) => muteOrUnmute(Constants.USER_UNMUTED, u));
  };

  // Bingo Engine Controls
  const newGame = async () => {
    bingoGame.current = new Bingo();
    bingoCards.current = {};
    setLastBall(null);
    setBallsChosen('');

    eventUsers.forEach((u) => {
      bingoCards.current[u.email] = new Card(u.email);
    });

    await setEventStatus({ status: Constants.EVENT_BINGO_GAME });
    setOpenConfirmNewGameDialog(false);
  };

  const onNewGame = () => {
    if (lastBall !== null) {
      setOpenConfirmNewGameDialog(true);
    } else {
      newGame();
    }
  };

  const pickBall = async () => {
    if (!bingoGame.current) return;
    const currBall = bingoGame.current.chooseBall();
    setLastBall(currBall);
    setBallsChosen((prev) => `${prev}${currBall}, `);

    Object.keys(bingoCards.current).forEach((email) => {
      bingoCards.current[email].ballChosen(currBall);
    });

    await setEventStatus({ status: Constants.EVENT_BINGO_BALL, ball: currBall });
  };

  const getBingoCardString = (eventUser) => {
    const card = bingoCards.current[eventUser.email];
    if (!card) return null;

    return (
      <div>
        &nbsp;B&nbsp;&nbsp;&nbsp;&nbsp;I&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;N&nbsp;&nbsp;&nbsp;&nbsp;G&nbsp;&nbsp;&nbsp;O
        <br />
        {card.getRow(0)}
        <br />
        {card.getRow(1)}
        <br />
        {card.getRow(2)}
        <br />
        {card.getRow(3)}
        <br />
        {card.getRow(4)}
        <br />
      </div>
    );
  };

  // Cleanup on Component Unmount
  useEffect(() => {
    return () => {
      endConference();
    };
  }, [endConference]);

  // Action Guards & UI Text Helpers
  const onCancelYes = async () => {
    if (conferenceStarted) {
      endConference();
    }
    await setEventStatus({ status: Constants.EVENT_CANCELLED });
    setOpenConfirmCancelDialog(false);
  };

  const getTitle = () => {
    if (!scheduledEvent) return '';
    const shownOn = DateTime.fromISO(scheduledEvent.shownOn)
      .setZone(user?._site?.timezone)
      .toFormat(Constants.TIME_FORMAT_LUXON);
    const statusText = Constants.EVENT_STATUS_TEXT[status] || '';
    return `${scheduledEvent._event?.displayName || ''} ${shownOn} ${statusText}`;
  };

  const getPotsStatusText = (eventUser) => {
    const phone = getPhoneNumber(eventUser);
    const email = eventUser.email;
    return potsStatus[phone] || potsStatus[email] || 'unknown';
  };

  const isSpeaking = (eventUser) => {
    return potsSpeaker === getPhoneNumber(eventUser);
  };

  const canStartNewGame = () => {
    return (
      status === Constants.EVENT_STOPPED ||
      status === Constants.EVENT_BINGO_GAME ||
      status === Constants.EVENT_BINGO_BALL ||
      status === Constants.EVENT_TELECON_STARTED
    );
  };

  const canPickBall = () => {
    return (
      status === Constants.EVENT_BINGO_GAME ||
      status === Constants.EVENT_BINGO_BALL ||
      status === Constants.EVENT_TELECON_STARTED
    );
  };

  const canCancel = () => {
    return (
      status === Constants.EVENT_STOPPED ||
      status === Constants.EVENT_BINGO_GAME ||
      status === Constants.EVENT_BINGO_BALL ||
      status === Constants.EVENT_TELECON_STARTED
    );
  };

  const canMute = () => {
    return (
      status !== Constants.EVENT_CANCELLED &&
      status !== Constants.EVENT_NOT_READY &&
      status !== Constants.EVENT_ABOUT_TO_START
    );
  };

  const canStartConference = () => {
    return (
      !conferenceStarted &&
      (status === Constants.EVENT_STOPPED ||
        status === Constants.EVENT_BINGO_GAME ||
        status === Constants.EVENT_TELECON_STARTED)
    );
  };

  const renderPhoneButtonCell = (eventUser) => {
    if (eventUser.useTwilio) {
      return <TableCell />;
    }

    const phone = getPhoneNumber(eventUser);
    const isHungUp = !!hungUpPhones[phone];

    return (
      <TableCell>
        <Button
          className="UserButton"
          variant="contained"
          color="primary"
          disabled={!conferenceStarted}
          onClick={() => (isHungUp ? redial(eventUser) : hangup(eventUser))}
        >
          {isHungUp ? 'ReDial' : 'Hangup'}
        </Button>
      </TableCell>
    );
  };

  const renderMuteCell = (eventUser) => {
    const isMuted = !!hasMuted[eventUser.email];
    const targetStatus = isMuted ? Constants.USER_UNMUTED : Constants.USER_MUTED;

    return (
      <TableCell>
        <Button
          className="UserButton"
          variant="contained"
          color="primary"
          disabled={!conferenceStarted}
          onClick={() => muteOrUnmute(targetStatus, eventUser)}
        >
          {isMuted ? 'UnMute' : 'Mute'}
        </Button>
      </TableCell>
    );
  };

  return (
    <div className="BingoEventDetails">
      <Typography variant="h6" gutterBottom>
        {getTitle()}
      </Typography>
      <Typography variant="subtitle1">
        Ball: {lastBall !== null ? lastBall.toString() : ''}
      </Typography>
      <Typography variant="subtitle1">
        Balls Chosen: {ballsChosen}
      </Typography>

      <div className="HorizontalContainer">
        <Button
          className="Button"
          variant="contained"
          color="primary"
          disabled={!canStartNewGame()}
          onClick={onNewGame}
        >
          New Game
        </Button>
        <Button
          className="Button"
          variant="contained"
          color="primary"
          disabled={!canPickBall()}
          onClick={pickBall}
        >
          Pick Ball
        </Button>
        <Button
          className="Button"
          variant="contained"
          color="primary"
          disabled={!canCancel()}
          onClick={() => setOpenConfirmCancelDialog(true)}
        >
          Cancel
        </Button>
        <Button
          className="Button"
          variant="contained"
          color="primary"
          disabled={!canStartConference()}
          onClick={startConference}
        >
          Start Telecon
        </Button>
        <Button
          className="Button"
          variant="contained"
          color="primary"
          disabled={!conferenceStarted}
          onClick={() => setOpenConfirmEndConferenceDialog(true)}
        >
          End Telecon
        </Button>
        <Button
          className="Button"
          variant="contained"
          color="primary"
          disabled={!canMute()}
          onClick={muteAll}
        >
          Mute All
        </Button>
        <Button
          className="Button"
          variant="contained"
          color="primary"
          disabled={!canMute()}
          onClick={unMuteAll}
        >
          UnMute All
        </Button>
      </div>

      <TableContainer className="TableContainer" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%' }}>Name</TableCell>
              <TableCell sx={{ width: '10%' }}>Phone</TableCell>
              <TableCell sx={{ width: '10%' }}>Status</TableCell>
              <TableCell sx={{ width: '20%' }}>Card</TableCell>
              <TableCell sx={{ width: '10%' }}>Dial</TableCell>
              <TableCell sx={{ width: '10%' }}>Mute</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eventUsers.map((eventUser) => (
              <TableRow key={eventUser._id || eventUser.email}>
                <TableCell>{`${eventUser.first} ${eventUser.last}`}</TableCell>
                <TableCell style={isSpeaking(eventUser) ? { color: 'red' } : {}}>
                  {getPhoneNumber(eventUser)}
                </TableCell>
                <TableCell>{getPotsStatusText(eventUser)}</TableCell>
                <TableCell>{getBingoCardString(eventUser)}</TableCell>
                {renderPhoneButtonCell(eventUser)}
                {renderMuteCell(eventUser)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ErrorDialog
        open={openErrorDialog}
        onErrorDialogClose={() => setOpenErrorDialog(false)}
        errorMessage={errorMessage}
      />
      <YesNoDialog
        open={openConfirmCancelDialog}
        onErrorDialogClose={() => setOpenConfirmCancelDialog(false)}
        onYes={onCancelYes}
        title="Cancel Event"
        message="Are you sure you want to cancel this event?"
      />
      <YesNoDialog
        open={openConfirmNewGameDialog}
        onErrorDialogClose={() => setOpenConfirmNewGameDialog(false)}
        onYes={newGame}
        title="New Game"
        message="Are you sure you want to start a new game?"
      />
      <YesNoDialog
        open={openConfirmEndConferenceDialog}
        onErrorDialogClose={() => setOpenConfirmEndConferenceDialog(false)}
        onYes={endConference}
        title="End Teleconference"
        message="Are you sure you want to end the conference?"
      />
    </div>
  );
};

export default BingoEvent;